import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { getUnreadNotificationCount } from '../services/apiClient';
import { useAuth } from './AuthContext';
import { useStreamingState } from './StreamingContext';
import { useImpersonation } from './ImpersonationContext';

/**
 * NotificationContext - Global notification state for badge count and polling
 *
 * Provides:
 * - Real-time unread count for sidebar badge
 * - Automatic polling every 60 seconds when tab is visible
 * - Refresh on tab focus
 * - Manual refresh trigger for after read/delete actions
 */

interface NotificationContextValue {
  /** Number of unread notifications */
  unreadCount: number;
  /** Whether count is currently being fetched */
  isLoading: boolean;
  /** Manually refresh the unread count */
  refreshUnreadCount: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

const POLLING_INTERVAL = 60000; // 60 seconds

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { authStatus } = useAuth();
  const { isStreaming } = useStreamingState();
  const { activeAlias } = useImpersonation();
  const prevAliasRef = useRef<typeof activeAlias | undefined>(undefined);

  const refreshUnreadCount = useCallback(async () => {
    if (!authStatus?.authenticated) {
      setUnreadCount(0);
      return;
    }

    setIsLoading(true);
    try {
      const response = await getUnreadNotificationCount();
      if (response.success && response.data) {
        const count = response.data.count;
        if (typeof count === 'number' && isFinite(count)) {
          setUnreadCount(count);
        } else {
          console.warn('[NotificationContext] Unexpected count value:', count);
          setUnreadCount(0);
        }
      }
    } catch (error) {
      console.error('[NotificationContext] Failed to fetch unread count:', error);
    } finally {
      setIsLoading(false);
    }
  }, [authStatus?.authenticated]);

  // Initial fetch when authenticated
  useEffect(() => {
    if (authStatus?.authenticated) {
      refreshUnreadCount();
    } else {
      setUnreadCount(0);
    }
  }, [authStatus?.authenticated, refreshUnreadCount]);

  // Refresh when impersonation changes (skip initial mount to avoid duplicate fetch)
  useEffect(() => {
    // Only run when activeAlias actually changes, not on initial mount
    if (prevAliasRef.current !== undefined && prevAliasRef.current !== activeAlias) {
      if (authStatus?.authenticated) {
        setUnreadCount(0); // Reset immediately
        refreshUnreadCount();
      }
    }
    prevAliasRef.current = activeAlias;
  }, [activeAlias, authStatus?.authenticated, refreshUnreadCount]);

  // Polling every 60 seconds when tab is visible and NOT streaming
  useEffect(() => {
    if (!authStatus?.authenticated) return;

    const intervalId = setInterval(() => {
      // Skip polling if SSE streaming is active to avoid interference
      if (document.visibilityState === 'visible' && !isStreaming) {
        refreshUnreadCount();
      }
    }, POLLING_INTERVAL);

    return () => clearInterval(intervalId);
  }, [authStatus?.authenticated, refreshUnreadCount, isStreaming]);

  // Refresh on tab focus (visibility change) - skip if streaming
  useEffect(() => {
    if (!authStatus?.authenticated) return;

    const handleVisibilityChange = () => {
      // Skip refresh if SSE streaming is active to avoid interference
      if (document.visibilityState === 'visible' && !isStreaming) {
        refreshUnreadCount();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [authStatus?.authenticated, refreshUnreadCount, isStreaming]);

  const value: NotificationContextValue = {
    unreadCount,
    isLoading,
    refreshUnreadCount,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

/**
 * Hook to access notification context
 * @throws Error if used outside NotificationProvider
 */
export function useNotificationContext(): NotificationContextValue {
  const context = useContext(NotificationContext);

  if (context === undefined) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }

  return context;
}
